import { ConfigProvider, theme } from "antd";
import {
  type PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";

type ColorModeContextType = {
  mode: string;
  setMode: (mode: string) => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

const lightTheme = {
  colorPrimary: '#FF3131',
  colorLink: '#FF3131',
  colorSuccess: '#52c41a',
  colorError: '#f5222d',
  colorWarning: '#faad14',
  fontFamily: "'Nunito Sans', sans-serif",
};

const darkTheme = {
  colorPrimary: '#FF3131',
  colorLink: '#ff9800',
  colorSuccess: '#87d068',
  colorError: '#cf1322',
  colorWarning: '#faad14',
  fontFamily: "'Nunito Sans', sans-serif",
};

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(
    colorModeFromLocalStorage || systemPreference
  );

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  const setColorMode = () => {
    setMode(mode === "light" ? "dark" : "light");
  };

  const { darkAlgorithm, defaultAlgorithm } = theme;

  const currentTheme = mode === "light" ? lightTheme : darkTheme;


  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ConfigProvider
        // you can change the theme colors here. example: ...RefineThemes.Magenta,
        theme={{
          token: currentTheme,
          algorithm: mode === "light" ? defaultAlgorithm : darkAlgorithm,
        }}
      >
        {children}
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
};
